import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface EffectiveFeature {
  feature_key: string;
  enabled: boolean;
  source: 'override' | 'producer' | 'plan' | 'missing';
  label_sv: string | null;
  description_sv: string | null;
  limit_value: number | null;
}

export type EffectiveFeatureMap = Record<string, EffectiveFeature>;

/**
 * Fetches resolved features for a producer using the RPC:
 *   get_producer_effective_features
 *
 * Priority chain: override > producer > plan > missing
 * This is the same SoT the admin panel uses.
 */
export function useProducerEffectiveFeatures(producerId: string | undefined) {
  const query = useQuery({
    queryKey: ['producer-effective-features', producerId],
    queryFn: async (): Promise<EffectiveFeatureMap> => {
      if (!producerId) return {};

      const { data, error } = await supabase.rpc('get_producer_effective_features', {
        p_producer_id: producerId,
      });

      if (error) {
        console.error('Failed to fetch effective features:', error);
        throw error;
      }

      const map: EffectiveFeatureMap = {};
      for (const row of data ?? []) {
        if (row.feature_key) {
          map[row.feature_key] = {
            feature_key: row.feature_key,
            enabled: row.effective_enabled ?? false,
            source: (row.source_kind as EffectiveFeature['source']) ?? 'missing',
            label_sv: row.label_sv,
            description_sv: row.description_sv,
            limit_value: row.effective_limit_value,
          };
        }
      }
      return map;
    },
    enabled: !!producerId,
    staleTime: 30_000,
  });

  /** Check if a specific feature is enabled */
  const isFeatureEnabled = (featureKey: string): boolean =>
    query.data?.[featureKey]?.enabled === true;

  /** Get the list of enabled feature keys (for backwards compat) */
  const enabledFeatureKeys: string[] = Object.values(query.data ?? {})
    .filter((f) => f.enabled)
    .map((f) => f.feature_key);

  return {
    featureMap: query.data ?? {},
    enabledFeatureKeys,
    isFeatureEnabled,
    isLoading: query.isLoading,
    error: query.error,
  };
}
